import { makeAutoObservable, runInAction, type AnnotationsMap } from "mobx";

import type {
  PaginatedRecordsResponseDTO,
  RecordListItemDTO,
  RecordsFiltersDTO,
} from "@/application/records/dtos/RecordListItemDTO";
import type {
  FetchAdminRecordsPaginatedUseCase,
  FetchRecordsPaginatedUseCase,
} from "@/application/records/use-cases";
import { RecordsStateManager } from "./RecordsStateManager";
import type { RecordsStateChangeEvent } from "./types/RecordsStateTypes";
import { getAuthSessionId } from "@/infrastructure/auth/AuthSessionManager";

export type RecordsListMode = "user" | "admin";

export interface RecordsListStoreDependencies {
  mode: RecordsListMode;
  fetchUserPaginatedUseCase: FetchRecordsPaginatedUseCase;
  fetchAdminPaginatedUseCase: FetchAdminRecordsPaginatedUseCase;
  stateManager?: RecordsStateManager;
}

export interface RecordsListQueryResult {
  data: {
    pages: PaginatedRecordsResponseDTO[];
    pageParams: number[];
  } | null;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: Error | null;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<void>;
  refetch: () => Promise<void>;
  total: number;
  filters: RecordsFiltersDTO;
}

class RecordsPaginationState {
  readonly limit: number;
  private pagesInternal: PaginatedRecordsResponseDTO[] = [];
  private filtersInternal: RecordsFiltersDTO = {};
  private totalInternal = 0;
  private versionInternal = 0;
  isLoading = false;
  isFetchingNextPage = false;
  error: string | null = null;
  initialized = false;

  constructor(limit: number) {
    this.limit = limit;
    makeAutoObservable(this, { limit: false }, { autoBind: true });
  }

  get pages(): PaginatedRecordsResponseDTO[] {
    return this.pagesInternal;
  }

  set pages(nextPages: PaginatedRecordsResponseDTO[]) {
    this.pagesInternal = nextPages;
  }

  get filters(): RecordsFiltersDTO {
    return { ...this.filtersInternal };
  }

  setFilters(filters: RecordsFiltersDTO): void {
    this.filtersInternal = { ...filters };
  }

  get total(): number {
    return this.totalInternal;
  }

  setTotal(total: number): void {
    if (this.totalInternal !== total) {
      this.totalInternal = total;
      this.incrementVersion();
      return;
    }
    this.totalInternal = total;
  }

  get currentPage(): number {
    if (!this.pagesInternal.length) {
      return 0;
    }
    return this.pagesInternal[this.pagesInternal.length - 1]?.page ?? 0;
  }

  get hasMore(): boolean {
    if (!this.pagesInternal.length) {
      return false;
    }
    return this.pagesInternal[this.pagesInternal.length - 1]?.has_more ?? false;
  }

  get flattenedRecords(): RecordListItemDTO[] {
    const seen = new Set<string>();
    const result: RecordListItemDTO[] = [];

    for (const page of this.pagesInternal) {
      for (const record of page.records) {
        if (!record?.id || seen.has(record.id)) {
          continue;
        }
        seen.add(record.id);
        result.push(record);
      }
    }

    return result;
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  setFetchingNext(value: boolean) {
    this.isFetchingNextPage = value;
  }

  setError(message: string | null) {
    this.error = message;
  }

  setInitialized(value: boolean) {
    this.initialized = value;
  }

  reset() {
    this.incrementVersion();
    this.pagesInternal = [];
    this.isLoading = false;
    this.isFetchingNextPage = false;
    this.error = null;
    this.initialized = false;
    this.totalInternal = 0;
  }

  updatePages(
    transform: (
      page: PaginatedRecordsResponseDTO
    ) => PaginatedRecordsResponseDTO
  ) {
    this.pagesInternal = this.pagesInternal.map(transform);
    this.incrementVersion();
  }

  replacePages(pages: PaginatedRecordsResponseDTO[]) {
    this.pagesInternal = pages;
    this.incrementVersion();
  }

  get version(): number {
    return this.versionInternal;
  }

  private incrementVersion(): void {
    this.versionInternal += 1;
  }
}

export class RecordsListStore {
  private readonly mode: RecordsListMode;
  private readonly fetchUserPaginatedUseCase: FetchRecordsPaginatedUseCase;
  private readonly fetchAdminPaginatedUseCase: FetchAdminRecordsPaginatedUseCase;
  private readonly stateManager?: RecordsStateManager;
  private readonly paginationStates = new Map<number, RecordsPaginationState>();
  private readonly queryResultCache = new Map<
    number,
    {
      result: RecordsListQueryResult;
      version: number;
    }
  >();
  private stateUnsubscriber?: () => void;

  constructor(deps: RecordsListStoreDependencies) {
    this.mode = deps.mode;
    this.fetchUserPaginatedUseCase = deps.fetchUserPaginatedUseCase;
    this.fetchAdminPaginatedUseCase = deps.fetchAdminPaginatedUseCase;
    this.stateManager = deps.stateManager;

    makeAutoObservable(
      this,
      {
        queryResultCache: false,
        stateUnsubscriber: false,
      } as AnnotationsMap<RecordsListStore, never>,
      { autoBind: true }
    );

    if (this.stateManager) {
      this.stateUnsubscriber = this.stateManager.subscribe(
        this.handleStateUpdate
      );
    }
  }

  dispose(): void {
    this.stateUnsubscriber?.();
    this.stateUnsubscriber = undefined;
  }

  private invalidateCache(limit: number): void {
    this.queryResultCache.delete(limit);
  }

  private invalidateAllCaches(): void {
    this.queryResultCache.clear();
  }

  private normalizeFilters(filters?: RecordsFiltersDTO): RecordsFiltersDTO {
    if (!filters) {
      return {};
    }

    const normalized: RecordsFiltersDTO = {};

    if (filters.search && filters.search.trim()) {
      normalized.search = filters.search.trim();
    }

    if (filters.status && filters.status.length > 0) {
      normalized.status = filters.status
        .filter((value) => Boolean(value) && value !== "all")
        .map((value) => value!);
    }

    if (filters.type && filters.type.length > 0) {
      const normalizedTypes = filters.type
        .filter((value) => Boolean(value) && value !== "all")
        .map((value) => value!.toLowerCase());
      normalized.type = Array.from(new Set(normalizedTypes));
    }

    if (filters.archived !== undefined) {
      normalized.archived = filters.archived;
    }

    if (filters.email && filters.email.trim()) {
      normalized.email = filters.email.trim();
    }

    return normalized;
  }

  private filtersKey(filters: RecordsFiltersDTO): string {
    const sortable: RecordsFiltersDTO = { ...filters };
    if (sortable.status) {
      sortable.status = [...sortable.status].sort();
    }
    if (sortable.type) {
      sortable.type = [...sortable.type].sort();
    }
    return JSON.stringify(sortable);
  }

  private areFiltersEqual(a: RecordsFiltersDTO, b: RecordsFiltersDTO): boolean {
    return this.filtersKey(a) === this.filtersKey(b);
  }

  private getFetchUseCase():
    | FetchRecordsPaginatedUseCase
    | FetchAdminRecordsPaginatedUseCase {
    return this.mode === "admin"
      ? this.fetchAdminPaginatedUseCase
      : this.fetchUserPaginatedUseCase;
  }

  private getOrCreateState(limit: number): RecordsPaginationState {
    const normalizedLimit = limit || 50;
    let state = this.paginationStates.get(normalizedLimit);
    if (!state) {
      state = new RecordsPaginationState(normalizedLimit);
      this.paginationStates.set(normalizedLimit, state);
    }
    return state;
  }

  async ensureInitialLoad(
    limit: number,
    enabled: boolean,
    filters?: RecordsFiltersDTO
  ): Promise<void> {
    if (!enabled) {
      return;
    }
    const normalizedFilters = this.normalizeFilters(filters);
    const state = this.getOrCreateState(limit);

    if (!this.areFiltersEqual(state.filters, normalizedFilters)) {
      state.setFilters(normalizedFilters);
      state.reset();
      this.invalidateCache(limit);
    }

    if (state.initialized || state.isLoading) {
      return;
    }
    await this.loadPage(state, 1, {
      replace: true,
      filters: normalizedFilters,
    });
  }

  async fetchNext(limit: number): Promise<void> {
    const state = this.getOrCreateState(limit);
    if (!state.initialized) {
      await this.loadPage(state, 1, { replace: true });
      return;
    }
    if (!state.hasMore || state.isFetchingNextPage) {
      return;
    }
    await this.loadPage(state, state.currentPage + 1, { append: true });
  }

  async refetch(limit: number): Promise<void> {
    const state = this.getOrCreateState(limit);
    await this.loadPage(state, 1, { replace: true, force: true });
  }

  async setFilters(
    limit: number,
    filters: RecordsFiltersDTO,
    options: { enabled: boolean }
  ): Promise<void> {
    const normalizedFilters = this.normalizeFilters(filters);
    const state = this.getOrCreateState(limit);

    const filtersChanged = !this.areFiltersEqual(
      state.filters,
      normalizedFilters
    );

    if (filtersChanged) {
      state.setFilters(normalizedFilters);
      state.reset();
      this.invalidateCache(limit);
    }

    if (!options.enabled) {
      return;
    }

    if (!state.initialized || filtersChanged) {
      await this.loadPage(state, 1, {
        replace: true,
        filters: normalizedFilters,
      });
    }
  }

  getQueryResult(limit: number): RecordsListQueryResult {
    const state = this.getOrCreateState(limit);

    // Create a version number that reflects modifications of the pagination state
    const version = state.version;

    // Check cache
    const cached = this.queryResultCache.get(limit);
    if (cached && cached.version === version) {
      return cached.result;
    }

    // Create new result
    const result: RecordsListQueryResult = {
      data: state.initialized
        ? {
            pages: state.pages.slice(),
            pageParams: state.pages.map((page) => page.page),
          }
        : null,
      isLoading: state.isLoading && !state.initialized,
      isFetchingNextPage: state.isFetchingNextPage,
      error: state.error ? new Error(state.error) : null,
      hasNextPage: state.hasMore,
      fetchNextPage: () => this.fetchNext(limit),
      refetch: () => this.refetch(limit),
      total: state.total,
      filters: state.filters,
    };

    // Cache it
    this.queryResultCache.set(limit, { result, version });

    return result;
  }

  invalidateAll(): void {
    this.paginationStates.forEach((state, limit) => {
      if (state.initialized) {
        void this.loadPage(state, 1, {
          replace: true,
          force: true,
          preserveExisting: true,
        });
      } else {
        state.reset();
        this.invalidateCache(limit);
      }
    });
    this.invalidateAllCaches();
  }

  /**
   * Completely clears all pagination states without triggering reloads.
   * Useful when switching auth sessions to immediately remove stale data.
   */
  resetAllStates(): void {
    this.paginationStates.forEach((state, limit) => {
      state.reset();
      this.invalidateCache(limit);
    });
    this.invalidateAllCaches();
  }

  async invalidateAndReload(): Promise<void> {
    // Clear cache but don't reset state
    this.invalidateAllCaches();

    // Force reload all initialized states
    const reloadPromises: Promise<void>[] = [];
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.paginationStates.forEach((state, _limit) => {
      if (state.initialized) {
        // Reload first page to get fresh data
        reloadPromises.push(
          this.loadPage(state, 1, { replace: true, force: true })
        );
      }
    });

    await Promise.all(reloadPromises);
  }

  updateRecordById(
    recordId: string,
    updates: Partial<RecordListItemDTO>
  ): boolean {
    let wasUpdated = false;

    this.paginationStates.forEach((state) => {
      if (!state.initialized) {
        return;
      }

      const pages = state.pages;
      let stateUpdated = false;

      const nextPages = pages.map((page) => {
        let pageUpdated = false;
        const nextRecords = page.records.map((record: RecordListItemDTO) => {
          if (record.id === recordId) {
            pageUpdated = true;
            wasUpdated = true;
            return { ...record, ...updates };
          }
          return record;
        });

        if (!pageUpdated) {
          return page;
        }

        stateUpdated = true;
        return {
          ...page,
          records: nextRecords,
        };
      });

      if (stateUpdated) {
        state.replacePages(nextPages);
      }
    });

    if (wasUpdated) {
      // Invalidate cached query snapshots so observers receive updated data
      this.invalidateAllCaches();
    }

    return wasUpdated;
  }

  createSnapshot(): Map<number, PaginatedRecordsResponseDTO[]> {
    const snapshot = new Map<number, PaginatedRecordsResponseDTO[]>();
    this.paginationStates.forEach((state, limit) => {
      if (!state.initialized) {
        return;
      }
      const pagesClone = state.pages.map((page) => ({
        ...page,
        records: page.records.map((record) => ({ ...record })),
      }));
      snapshot.set(limit, pagesClone);
    });
    return snapshot;
  }

  restoreSnapshot(snapshot: Map<number, PaginatedRecordsResponseDTO[]>): void {
    snapshot.forEach((pages, limit) => {
      const state = this.paginationStates.get(limit);
      if (!state) {
        return;
      }
      state.replacePages(pages);
      state.setInitialized(true);
      this.invalidateCache(limit);
    });
  }

  private handleStateUpdate(change: RecordsStateChangeEvent): void {
    const hasUpdates = change.updatedRecords.length > 0;
    const hasRemovals = change.removedRecordIds.length > 0;

    if (!hasUpdates && !hasRemovals) {
      return;
    }

    const updates = hasUpdates
      ? new Map(
          change.updatedRecords
            .filter((record) => Boolean(record?.id))
            .map((record) => [record.id, record])
        )
      : undefined;
    const removedIds = hasRemovals
      ? new Set(change.removedRecordIds)
      : undefined;

    this.paginationStates.forEach((state, limit) => {
      if (!state.initialized) {
        return;
      }

      let stateChanged = false;
      let removedFromState = 0;

      const nextPages = state.pages.map((page) => {
        let pageChanged = false;
        let removedFromPage = 0;
        const nextRecords: RecordListItemDTO[] = [];

        for (const record of page.records) {
          if (removedIds?.has(record.id)) {
            pageChanged = true;
            removedFromPage += 1;
            continue;
          }

          if (!updates) {
            nextRecords.push(record);
            continue;
          }

          const updated = updates.get(record.id);
          if (!updated || !this.shouldApplyUpdate(record, updated)) {
            nextRecords.push(record);
            continue;
          }

          pageChanged = true;
          nextRecords.push({ ...record, ...updated });
        }

        if (!pageChanged) {
          return page;
        }

        stateChanged = true;
        removedFromState += removedFromPage;

        const nextTotal =
          typeof page.total === "number"
            ? Math.max(0, page.total - removedFromPage)
            : page.total;

        return {
          ...page,
          records: nextRecords,
          total: nextTotal,
          has_more: page.has_more,
        };
      });

      if (stateChanged) {
        state.replacePages(nextPages);
        if (removedFromState > 0) {
          state.setTotal(Math.max(0, state.total - removedFromState));
        }
        this.invalidateCache(limit);
      }
    });
  }

  private shouldApplyUpdate(
    existing: RecordListItemDTO,
    updated: RecordListItemDTO
  ): boolean {
    return (
      existing.project !== updated.project ||
      existing.name !== updated.name ||
      existing.status !== updated.status ||
      existing.type !== updated.type ||
      existing.is_archived !== updated.is_archived ||
      existing.is_public !== updated.is_public ||
      existing.modified_date !== updated.modified_date ||
      existing.participants !== updated.participants ||
      existing.participants_count !== updated.participants_count ||
      existing.size_mb !== updated.size_mb
    );
  }

  removeRecords(recordIds: string[]): void {
    if (!recordIds.length) {
      return;
    }
    const idSet = new Set(recordIds);
    this.paginationStates.forEach((state, limit) => {
      if (!state.initialized) {
        return;
      }

      const updatedPages = state.pages.map((page) => {
        const filtered = page.records.filter((record) => !idSet.has(record.id));
        return {
          ...page,
          records: filtered,
          total: Math.max(
            0,
            page.total - (page.records.length - filtered.length)
          ),
          has_more:
            filtered.length === state.limit ? page.has_more : page.has_more,
        };
      });

      state.replacePages(updatedPages);
      const firstPage = updatedPages[0];
      if (firstPage) {
        state.setTotal(firstPage.total ?? state.total);
      }
      this.invalidateCache(limit);
    });
  }

  toggleArchive(recordIds: string[]): void {
    if (!recordIds.length) {
      return;
    }
    const idSet = new Set(recordIds);
    this.paginationStates.forEach((state, limit) => {
      if (!state.initialized) {
        return;
      }

      const updatedPages = state.pages.map((page) => ({
        ...page,
        records: page.records.map((record) =>
          idSet.has(record.id)
            ? { ...record, is_archived: !record.is_archived }
            : record
        ),
      }));

      state.replacePages(updatedPages);
      this.invalidateCache(limit);
    });
  }

  renameRecord(recordId: string, newName: string): void {
    if (!recordId) {
      return;
    }

    this.paginationStates.forEach((state, limit) => {
      if (!state.initialized) {
        return;
      }

      const updatedPages = state.pages.map((page) => ({
        ...page,
        records: page.records.map((record) =>
          record.id === recordId ? { ...record, name: newName } : record
        ),
      }));

      state.replacePages(updatedPages);
      this.invalidateCache(limit);
    });
  }

  private async loadPage(
    state: RecordsPaginationState,
    page: number,
    options: {
      append?: boolean;
      replace?: boolean;
      force?: boolean;
      filters?: RecordsFiltersDTO;
      preserveExisting?: boolean;
    }
  ): Promise<void> {
    const fetchUseCase = this.getFetchUseCase();
    const requestSessionId = getAuthSessionId();

    if (page === 1) {
      state.setLoading(true);
    } else {
      state.setFetchingNext(true);
    }
    state.setError(null);

    const requestFilters = this.normalizeFilters(
      options.filters ?? state.filters
    );
    const existingPages =
      options.replace && options.preserveExisting ? state.pages.slice() : [];

    try {
      const response = await fetchUseCase.execute({
        page,
        limit: state.limit,
        filters: requestFilters,
      });

      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        state.setFilters(requestFilters);
        state.setTotal(response.total ?? state.total);
        if (options.append) {
          state.replacePages([...state.pages, response]);
        } else if (
          options.replace &&
          options.preserveExisting &&
          page === 1 &&
          existingPages.length
        ) {
          const [, ...rest] = existingPages;
          state.replacePages([response, ...rest]);
        } else {
          state.replacePages([response]);
        }
        state.setInitialized(true);
      });
      if (response.records?.length) {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        this.stateManager?.upsertRecords(response.records);
      }
      this.invalidateCache(state.limit);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to load records";
      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        state.setError(message);
      });
      throw error;
    } finally {
      runInAction(() => {
        if (requestSessionId !== getAuthSessionId()) {
          return;
        }
        state.setLoading(false);
        state.setFetchingNext(false);
      });
    }
  }
}
